/**
 * EmailCompose Component
 *
 * Modal dialog for composing, replying to, and forwarding emails.
 * Uses React-Quill for rich text HTML editing.
 *
 * Supports:
 * - New email composition with HTML formatting
 * - Reply to single recipient
 * - Reply all
 * - Forward with original message
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sendApi, draftsApi, EmailDraft } from '@/services/emailApi';

export type ComposeMode = 'new' | 'reply' | 'reply-all' | 'forward' | 'draft';

// ==================== Email Content Utilities ====================

/**
 * Decode quoted-printable encoded content
 */
function decodeQuotedPrintable(input: string): string {
  if (!input) return '';
  return input
    .replace(/=\r?\n/g, '')
    .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
}

/**
 * Check if content is MIME multipart
 */
function isMimeMultipart(content: string): boolean {
  if (!content) return false;
  return /^--[-=_a-zA-Z0-9]+\r?\n/m.test(content) ||
         /----==_mimepart_/.test(content) ||
         /^------=_/.test(content);
}

/**
 * Parse MIME multipart and extract text/html parts
 */
function parseMimeMultipart(content: string): { html: string | null; text: string | null } {
  if (!content) return { html: null, text: null };

  const boundaryMatch = content.match(/^(--[-=_a-zA-Z0-9]+)\r?\n/m) ||
                        content.match(/(----==_mimepart_[a-zA-Z0-9_]+)/);
  if (!boundaryMatch) return { html: null, text: null };

  const boundary = boundaryMatch[1];
  const parts = content.split(boundary);
  let htmlPart: string | null = null;
  let textPart: string | null = null;

  for (const part of parts) {
    if (!part || part === '--' || part.trim() === '--') continue;
    const headerBodySplit = part.match(/^([\s\S]*?)\r?\n\r?\n([\s\S]*)$/);
    if (!headerBodySplit) continue;

    const headers = headerBodySplit[1].toLowerCase();
    let body = headerBodySplit[2];
    const isQuotedPrintableEncoded = headers.includes('quoted-printable');

    if (isQuotedPrintableEncoded) {
      body = decodeQuotedPrintable(body);
    }
    body = body.replace(/\r?\n--$/, '').trim();

    if (headers.includes('text/html') && !htmlPart) {
      htmlPart = body;
    } else if (headers.includes('text/plain') && !textPart) {
      textPart = body;
    }
  }

  return { html: htmlPart, text: textPart };
}

/**
 * Convert plain text to simple HTML
 */
function plainTextToHtml(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
    .replace(/  /g, '&nbsp; ');
}

/**
 * Extract HTML content from email body (handles MIME, QP encoding)
 */
function extractHtmlContent(bodyText?: string, bodyHtml?: string): string {
  // Prefer HTML body
  let content = bodyHtml || bodyText || '';

  // Handle MIME multipart
  if (isMimeMultipart(content)) {
    const parsed = parseMimeMultipart(content);
    // Prefer HTML part
    if (parsed.html) {
      content = parsed.html;
    } else if (parsed.text) {
      content = plainTextToHtml(parsed.text);
    }
  } else {
    // Decode quoted-printable if needed
    if (/=3D|=20|=\r?\n/.test(content)) {
      content = decodeQuotedPrintable(content);
    }
    // Convert plain text to HTML if it doesn't look like HTML
    if (!/<[^>]+>/.test(content)) {
      content = plainTextToHtml(content);
    }
  }

  return content;
}

interface EmailComposeProps {
  isOpen: boolean;
  onClose: () => void;
  mode: ComposeMode;

  // For reply/forward modes
  messageId?: string;
  threadId?: string;
  originalMessage?: {
    from_email: string;
    from_name?: string;
    to?: Array<{ email: string; name?: string }>;
    cc?: Array<{ email: string; name?: string }>;
    subject: string;
    body_text?: string;
    body_html?: string;
    received_at?: string;
  };

  // Required for new emails
  selectedAccountId: string;

  // Callback when email is sent
  onSent?: (messageId: string) => void;

  // For editing existing drafts
  editDraftId?: string;
  editDraft?: EmailDraft;

  // Callback when draft is saved
  onDraftSaved?: (draftId: string) => void;

  // Callback when draft is deleted (after send)
  onDraftDeleted?: (draftId: string) => void;
}

const EmailCompose: React.FC<EmailComposeProps> = ({
  isOpen,
  onClose,
  mode,
  messageId,
  originalMessage,
  selectedAccountId,
  onSent,
  editDraftId: _editDraftId, // Keep for API compatibility, use editDraft instead
  editDraft,
  onDraftSaved,
  onDraftDeleted,
}) => {
  const { t } = useTranslation('email');

  // Form fields
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  // UI state
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // Draft state
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  const toInputRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);

  // Quill editor modules and formats
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  }), []);

  const quillFormats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'link', 'image'
  ], []);

  // Initialize form based on mode when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setDraftId(null);
    setLastSaved(null);
    setHasUnsavedChanges(false);

    if (mode === 'draft' && editDraft) {
      // Load draft for editing
      setDraftId(editDraft.draft_id);
      setTo(editDraft.to?.join(', ') || '');
      if (editDraft.cc && editDraft.cc.length > 0) {
        setCc(editDraft.cc.join(', '));
        setShowCc(true);
      } else {
        setCc('');
        setShowCc(false);
      }
      if (editDraft.bcc && editDraft.bcc.length > 0) {
        setBcc(editDraft.bcc.join(', '));
        setShowBcc(true);
      } else {
        setBcc('');
        setShowBcc(false);
      }
      setSubject(editDraft.subject || '');
      setBodyHtml(editDraft.body_html || '');
      setTimeout(() => quillRef.current?.getEditor()?.focus(), 100);
    } else if (mode === 'new') {
      // Clear all fields for new email
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBodyHtml('');
      setShowCc(false);
      setShowBcc(false);
      // Focus To field
      setTimeout(() => toInputRef.current?.focus(), 100);
    } else if (mode === 'reply' && originalMessage) {
      // Reply: set To to original sender
      setTo(originalMessage.from_email);
      setCc('');
      setBcc('');
      setSubject(
        originalMessage.subject.startsWith('Re:')
          ? originalMessage.subject
          : `Re: ${originalMessage.subject}`
      );
      setBodyHtml(formatQuotedHtml(originalMessage));
      setShowCc(false);
      setShowBcc(false);
      // Focus editor for reply
      setTimeout(() => quillRef.current?.getEditor()?.focus(), 100);
    } else if (mode === 'reply-all' && originalMessage) {
      // Reply All: set To to original sender, Cc to all other recipients
      setTo(originalMessage.from_email);

      // Get all other recipients for Cc (excluding ourselves)
      const allRecipients = [
        ...(originalMessage.to || []),
        ...(originalMessage.cc || []),
      ]
        .filter((r) => r.email !== originalMessage.from_email)
        .map((r) => r.email);

      if (allRecipients.length > 0) {
        setCc(allRecipients.join(', '));
        setShowCc(true);
      } else {
        setCc('');
        setShowCc(false);
      }

      setBcc('');
      setSubject(
        originalMessage.subject.startsWith('Re:')
          ? originalMessage.subject
          : `Re: ${originalMessage.subject}`
      );
      setBodyHtml(formatQuotedHtml(originalMessage));
      setShowBcc(false);
      setTimeout(() => quillRef.current?.getEditor()?.focus(), 100);
    } else if (mode === 'forward' && originalMessage) {
      // Forward: clear To, set subject with Fwd prefix, include original message
      setTo('');
      setCc('');
      setBcc('');
      setSubject(
        originalMessage.subject.startsWith('Fwd:')
          ? originalMessage.subject
          : `Fwd: ${originalMessage.subject}`
      );
      setBodyHtml(formatForwardedHtml(originalMessage));
      setShowCc(false);
      setShowBcc(false);
      // Focus To field for forward
      setTimeout(() => toInputRef.current?.focus(), 100);
    }
  }, [isOpen, mode, originalMessage, editDraft]);

  // Format quoted HTML for replies
  const formatQuotedHtml = (msg: NonNullable<typeof originalMessage>) => {
    const date = msg.received_at
      ? new Date(msg.received_at).toLocaleString()
      : 'Unknown date';
    const sender = msg.from_name
      ? `${msg.from_name} &lt;${msg.from_email}&gt;`
      : msg.from_email;

    // Extract HTML content from original message
    const originalContent = extractHtmlContent(msg.body_text, msg.body_html);

    return `<br><br>
<div style="color: #666; border-left: 3px solid #ccc; padding-left: 12px; margin-left: 0;">
  <p style="margin: 0 0 8px 0;">On ${date}, ${sender} wrote:</p>
  <div>${originalContent}</div>
</div>`;
  };

  // Format HTML for forwards
  const formatForwardedHtml = (msg: NonNullable<typeof originalMessage>) => {
    const date = msg.received_at
      ? new Date(msg.received_at).toLocaleString()
      : 'Unknown date';
    const sender = msg.from_name
      ? `${msg.from_name} &lt;${msg.from_email}&gt;`
      : msg.from_email;
    const recipients = msg.to?.map((r) => r.name || r.email).join(', ') || '';

    // Extract HTML content from original message
    const originalContent = extractHtmlContent(msg.body_text, msg.body_html);

    return `<br><br>
<div style="border-top: 1px solid #ccc; padding-top: 12px;">
  <p style="color: #666; margin: 0 0 8px 0;"><strong>---------- Forwarded message ----------</strong></p>
  <p style="color: #666; margin: 2px 0;"><strong>From:</strong> ${sender}</p>
  <p style="color: #666; margin: 2px 0;"><strong>Date:</strong> ${date}</p>
  <p style="color: #666; margin: 2px 0;"><strong>Subject:</strong> ${msg.subject}</p>
  <p style="color: #666; margin: 2px 0 12px 0;"><strong>To:</strong> ${recipients}</p>
  <div>${originalContent}</div>
</div>`;
  };

  // Parse email addresses from comma-separated string
  const parseEmails = (input: string): string[] => {
    return input
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  // Save draft to backend
  const saveDraft = useCallback(async (silent = false) => {
    // Don't save empty drafts
    const hasContent = to.trim() || subject.trim() || (bodyHtml.trim() && bodyHtml !== '<p><br></p>');
    if (!hasContent) return;

    // Don't save if already saving
    if (isSavingDraft) return;

    setIsSavingDraft(true);
    try {
      const result = await draftsApi.save({
        account_id: selectedAccountId,
        draft_id: draftId || undefined,
        to: parseEmails(to),
        cc: showCc && cc ? parseEmails(cc) : undefined,
        bcc: showBcc && bcc ? parseEmails(bcc) : undefined,
        subject: subject.trim(),
        body_html: bodyHtml,
      });

      setDraftId(result.draft_id);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      if (!silent) {
        onDraftSaved?.(result.draft_id);
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      if (!silent) {
        setError(t('compose.save_draft_failed'));
      }
    } finally {
      setIsSavingDraft(false);
    }
  }, [to, cc, bcc, subject, bodyHtml, showCc, showBcc, selectedAccountId, draftId, isSavingDraft, onDraftSaved]);

  // Delete draft from backend
  const deleteDraft = useCallback(async (id: string) => {
    try {
      await draftsApi.delete(id);
      onDraftDeleted?.(id);
    } catch (err) {
      console.error('Failed to delete draft:', err);
      // Non-critical error, don't block the flow
    }
  }, [onDraftDeleted]);

  // Track changes for auto-save
  const markUnsaved = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  // Auto-save draft every 30 seconds when there are unsaved changes
  useEffect(() => {
    if (!isOpen || mode === 'reply' || mode === 'reply-all' || mode === 'forward') {
      // Don't auto-save replies/forwards (only new emails and draft edits)
      return;
    }

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Set up new auto-save timer if there are unsaved changes
    if (hasUnsavedChanges) {
      autoSaveTimerRef.current = setTimeout(() => {
        saveDraft(true); // silent auto-save
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isOpen, mode, hasUnsavedChanges, saveDraft]);

  // Validate form
  const validateForm = (): string | null => {
    const toEmails = parseEmails(to);
    if (toEmails.length === 0) {
      return t('compose.enter_recipient');
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of toEmails) {
      if (!emailRegex.test(email)) {
        return t('compose.invalid_email', { email });
      }
    }

    if (!subject.trim()) {
      return t('compose.enter_subject');
    }

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSending(true);

    try {
      let result: { message_id: string };

      if (mode === 'new' || mode === 'draft') {
        // Send new email or draft with HTML body
        result = await sendApi.send({
          account_id: selectedAccountId,
          to: parseEmails(to),
          cc: showCc && cc ? parseEmails(cc) : undefined,
          bcc: showBcc && bcc ? parseEmails(bcc) : undefined,
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      } else if (mode === 'reply' && messageId) {
        // Reply to email
        result = await sendApi.reply(messageId, {
          to: parseEmails(to),
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      } else if (mode === 'reply-all' && messageId) {
        // Reply all
        result = await sendApi.replyAll(messageId, {
          to: parseEmails(to),
          cc: showCc && cc ? parseEmails(cc) : undefined,
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      } else if (mode === 'forward' && messageId) {
        // Forward email
        result = await sendApi.forward(messageId, {
          to: parseEmails(to),
          cc: showCc && cc ? parseEmails(cc) : undefined,
          subject: subject.trim(),
          body_html: bodyHtml,
        });
      } else {
        throw new Error('Invalid compose mode or missing message ID');
      }

      // Delete draft if we were editing one
      if (draftId) {
        await deleteDraft(draftId);
      }

      // Success - close modal and notify parent
      onSent?.(result.message_id);
      handleClose();
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(
        err instanceof Error ? err.message : t('compose.send_failed')
      );
    } finally {
      setIsSending(false);
    }
  };

  // Handle close with confirmation if content exists
  const handleClose = () => {
    if (isSending || isSavingDraft) return;

    // Check if there's unsaved content
    const hasContent =
      to.trim() ||
      cc.trim() ||
      bcc.trim() ||
      (mode === 'new' && subject.trim()) ||
      (mode === 'new' && bodyHtml.trim() && bodyHtml !== '<p><br></p>');

    if (hasContent && (mode === 'new' || mode === 'draft') && hasUnsavedChanges) {
      // Confirm for new emails and drafts with unsaved changes
      const choice = confirm(t('compose.unsaved_changes_confirm'));
      if (choice) {
        // Save draft then close
        saveDraft(false).then(() => {
          resetAndClose();
        });
        return;
      }
    }

    resetAndClose();
  };

  // Reset form state and close modal
  const resetAndClose = () => {
    // Clear auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    // Reset form
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setBodyHtml('');
    setError(null);
    setShowCc(false);
    setShowBcc(false);
    setDraftId(null);
    setLastSaved(null);
    setHasUnsavedChanges(false);
    onClose();
  };

  // Get modal title based on mode
  const getTitle = () => {
    switch (mode) {
      case 'reply':
        return t('compose.reply');
      case 'reply-all':
        return t('compose.reply_all');
      case 'forward':
        return t('compose.forward');
      case 'draft':
        return t('compose.edit_draft');
      default:
        return t('compose.compose_email');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{getTitle()}</h3>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSending}
                className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-3">
              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* To Field */}
              <div className="flex items-center gap-2">
                <label htmlFor="email-to" className="text-sm font-medium text-gray-500 w-12">
                  To:
                </label>
                <input
                  ref={toInputRef}
                  type="text"
                  id="email-to"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); markUnsaved(); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="recipient@example.com"
                  disabled={isSending}
                />
                <div className="flex items-center gap-1 text-sm">
                  {!showCc && (
                    <button
                      type="button"
                      onClick={() => setShowCc(true)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Cc
                    </button>
                  )}
                  {!showBcc && (
                    <button
                      type="button"
                      onClick={() => setShowBcc(true)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Bcc
                    </button>
                  )}
                </div>
              </div>

              {/* Cc Field (optional) */}
              {showCc && (
                <div className="flex items-center gap-2">
                  <label htmlFor="email-cc" className="text-sm font-medium text-gray-500 w-12">
                    Cc:
                  </label>
                  <input
                    type="text"
                    id="email-cc"
                    value={cc}
                    onChange={(e) => { setCc(e.target.value); markUnsaved(); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="cc@example.com"
                    disabled={isSending}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowCc(false);
                      setCc('');
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Bcc Field (optional) */}
              {showBcc && (
                <div className="flex items-center gap-2">
                  <label htmlFor="email-bcc" className="text-sm font-medium text-gray-500 w-12">
                    Bcc:
                  </label>
                  <input
                    type="text"
                    id="email-bcc"
                    value={bcc}
                    onChange={(e) => { setBcc(e.target.value); markUnsaved(); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="bcc@example.com"
                    disabled={isSending}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowBcc(false);
                      setBcc('');
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {/* Subject Field */}
              <div className="flex items-center gap-2">
                <label htmlFor="email-subject" className="text-sm font-medium text-gray-500 w-12">
                  Subject:
                </label>
                <input
                  type="text"
                  id="email-subject"
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); markUnsaved(); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder={t('compose.email_subject_placeholder')}
                  disabled={isSending}
                />
              </div>

              {/* Rich Text Editor */}
              <div className="email-editor-container">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={bodyHtml}
                  onChange={(value) => { setBodyHtml(value); markUnsaved(); }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder={t('compose.write_message')}
                  readOnly={isSending}
                  style={{ minHeight: '300px' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-500 flex items-center gap-2">
                {mode !== 'new' && mode !== 'draft' && originalMessage && (
                  <span>
                    {mode === 'reply' || mode === 'reply-all' ? t('compose.replying_to') : t('compose.forwarding')}:{' '}
                    {originalMessage.from_name || originalMessage.from_email}
                  </span>
                )}
                {(mode === 'new' || mode === 'draft') && lastSaved && (
                  <span className="text-green-600">
                    Draft saved at {lastSaved.toLocaleTimeString()}
                  </span>
                )}
                {(mode === 'new' || mode === 'draft') && isSavingDraft && (
                  <span className="text-gray-500 flex items-center gap-1">
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    {t('compose.saving_draft')}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                {(mode === 'new' || mode === 'draft') && (
                  <button
                    type="button"
                    onClick={() => saveDraft(false)}
                    disabled={isSending || isSavingDraft}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                      />
                    </svg>
                    {t('compose.save_draft')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSending || isSavingDraft}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSending || isSavingDraft}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSending ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      {t('compose.sending')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        />
                      </svg>
                      {t('compose.send')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailCompose;
